import { useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Container } from "@egalregal/shared";
import { requests } from "../../shared/api";
import { log } from "../../logger";
import { useT } from "../../shared/i18n";
import { CONTAINER_DEADLINE_VOCABULARY, DeadlineChips } from "../../shared/components/Deadlines";
import { collectContainerMedia } from "../../shared/lib/containerMedia";
import { renderMarkdown } from "../../shared/lib/markdown";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { usePreferencesStore } from "../../shared/store/preferences";
import { useDemoMode } from "../demo/useDemoMode";
import { AiSummaryMarker } from "../items/AiSummaryMarker";
import { ContainerMediaGrid } from "../items/ContainerMediaGrid";

/**
 * The merged container view's details block (container-view 01): the container's
 * own content below the sub-container list, with no header of its own and no
 * editing controls.
 *
 * The shell's content header already owns the name, kind, crumbs and identity.
 * The expires-on **input**, the catalog-entry selector and Delete left this page
 * (spec: detail is read-first); the block paints the stored values and the
 * media only.
 */
export function ContainerDetails({ container }: { container: Container }) {
  const t = useT();
  const demoMode = useDemoMode();
  // The readable union, not the entry-scoped slice: the subtree's media comes
  // from every container the caller can read (container-media 02).
  const readable = useAppStore(readableContainers);
  const includeSubtreeMedia = usePreferencesStore((s) => s.containerSubtreeMedia);
  const containerKindFilter = usePreferencesStore((s) => s.containerKindFilter);
  const [notice, setNotice] = useState<DetailNotice | null>(null);
  const [reExtracting, setReExtracting] = useState(false);

  // Sub-container media is a device preference (container-media 02): derive the
  // merged collection from the in-memory readable containers, no extra request.
  const media = collectContainerMedia(readable, container, {
    includeDescendants: includeSubtreeMedia,
    kinds: containerKindFilter,
  });

  const summary = container.data?.llmSummary;
  // The chip follows the marker the apply stage owns, not value equality: a
  // field the user cleared and retyped to the old AI text is manual, so apply
  // will never refresh it and no chip may claim otherwise (ticket 11).
  const applied = container.data?.aiApplied;
  const isAiDescription = Boolean(
    applied?.summary !== undefined &&
      container.description &&
      applied.summary === container.description,
  );
  const isAiOwner = Boolean(
    applied?.owner !== undefined && container.owner && applied.owner === container.owner,
  );
  const hasProperties = Object.keys(container.properties ?? {}).length > 0;
  // The block is shown on its own when no scalar carries it: a manual
  // description/owner wins, but the AI block and its re-extract stay reachable
  // (ADR-0028 keeps the card away; this is the marker in block form).
  const showsStandaloneBlock = Boolean(summary) && !isAiDescription && !isAiOwner;

  const reExtract = async () => {
    if (!summary) return;
    setReExtracting(true);
    try {
      const result = await requests.containers.reExtract(container.id);
      // The route returns only `{ version }`: the trigger changes no state, and
      // the regenerated block arrives through SSE when the merge finishes.
      // Mirror the version so the next edit does not race a stale one.
      useAppStore.setState((state) => ({
        containers: state.containers.map((current) =>
          current.id === container.id ? { ...current, version: result.version } : current,
        ),
      }));
      setNotice({ message: t("aiSummaryReExtractQueued"), severity: "success" });
    } catch (err) {
      log.warn({ err, containerId: container.id }, "AI summary re-extract failed");
      setNotice({ message: t("aiSummaryReExtractFailed"), severity: "error" });
    } finally {
      setReExtracting(false);
    }
  };
  // Demo mode is read-only (ADR-0045): no write action is offered.
  const onReExtract = demoMode ? undefined : () => void reExtract();

  return (
    <Box
      data-testid="container-details"
      sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}
    >
      {container.description && (
        <Box>
          <Box
            component="div"
            data-testid="container-details-description"
            sx={{ "& p": { my: 1 }, "&:first-of-type p": { mt: 0 } }}
          >
            {renderMarkdown(container.description)}
          </Box>
          {isAiDescription && summary && (
            <AiSummaryMarker
              summary={summary}
              field="description"
              onReExtract={onReExtract}
              reExtracting={reExtracting}
            />
          )}
        </Box>
      )}

      {container.owner && (
        <Typography variant="body2" color="text.secondary">
          {t("formOwner")}: {container.owner}
          {isAiOwner && summary && (
            <AiSummaryMarker
              summary={summary}
              field="owner"
              onReExtract={onReExtract}
              reExtracting={reExtracting}
            />
          )}
        </Typography>
      )}

      {summary && showsStandaloneBlock && (
        <Box sx={{ mt: 1 }} data-testid="ai-summary-block">
          <AiSummaryMarker
            summary={summary}
            label="block"
            testId="ai-summary-block-marker"
            onReExtract={onReExtract}
            reExtracting={reExtracting}
          />
        </Box>
      )}

      {container.tags.length > 0 && (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
          {container.tags.map((tag) => (
            <Chip key={tag} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
      )}

      {hasProperties && (
        <Box sx={{ mt: 1 }} data-testid="container-date-chips">
          <DeadlineChips
            properties={container.properties}
            vocabulary={CONTAINER_DEADLINE_VOCABULARY}
          />
        </Box>
      )}

      <ContainerMediaGrid
        container={container}
        entries={media.entries}
        subtreeEmptyNote={media.subtreeEmpty}
        notify={setNotice}
      />

      <Snackbar
        open={notice !== null}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={notice?.severity ?? "success"} onClose={() => setNotice(null)}>
          <span data-testid="container-media-notice">{notice?.message}</span>
        </Alert>
      </Snackbar>
    </Box>
  );
}

interface DetailNotice {
  message: string;
  severity: "success" | "error";
}
